import React, {
  useReducer,
  useContext,
  useEffect,
  ReactNode,
  RefObject,
} from "react";
import { PayloadAction } from "redux-starter-kit";
import cloneDeep from "lodash.clonedeep";
import {
  EditComponentErrorResponse,
  FormSchemaDto,
} from "@amzn/crowd-front-end-common-library";
import { MetricsState } from "../logging";
import { FormState, actions, reducer } from "./formSlice";
import { validationErrorTextFormatter } from "./types";

// Prevents infinite rendering if no value provided from input.
const DEFAULT_EMPTY_VALUE = Object.freeze({});

type Dispatch = (action: PayloadAction) => void;

const FormStateContext = React.createContext<FormState<any> | undefined>(
  undefined
);
const FormDispatchContext = React.createContext<Dispatch | undefined>(
  undefined
);

/**
 * Interface for FormContextProvider properties.
 *
 * @export
 * @interface FormContextProviderProps
 * @template T
 */
export interface FormContextProviderProps<T> {
  savedValue?: Partial<T>;
  onFormValueChange?: (item: Partial<T>, diff: Partial<T>) => void;
  onFormSchemaChange?: (formSchema?: FormSchemaDto) => void;
  validationErrors?: EditComponentErrorResponse;
  customValidationErrorTextFormatter?: validationErrorTextFormatter<T>;
  metrics: MetricsState;
  formId: string;
  children: ReactNode[] | ReactNode;
  refCallback?: (propertyName: string) => RefObject<HTMLElement>;
}

function getShallowDiffObject(original: any, changed: any): any {
  return Object.keys(changed).reduce((diff: any, key: string) => {
    if (changed[key] !== original[key]) {
      diff[key] = changed[key];
    }

    return diff;
  }, {});
}

/**
 * Component that wraps a form. Handles the context of the form and centralizes common behavior.
 * Required for many other form elements.
 *
 * @template T
 * @param {FormContextProviderProps<T>} {
 *   currentValue = {},
 *   onFormValueChange,
 *   onFormSchemaChange
 *   validationErrors,
 *   customValidationErrorTextFormatter,
 *   formId,
 *   metrics,
 *   itemType,
 *   children,
 * }
 * @returns {JSX.Element}
 */
function FormContextProvider<T extends object>({
  savedValue = DEFAULT_EMPTY_VALUE,
  onFormValueChange,
  onFormSchemaChange,
  validationErrors,
  customValidationErrorTextFormatter,
  formId,
  metrics,
  children,
  refCallback,
}: FormContextProviderProps<T>): JSX.Element {
  const [state, dispatch] = useReducer<
    React.Reducer<FormState<T>, PayloadAction>
  >(reducer, {
    originalValue: savedValue,
    currentValue: savedValue,
    metrics,
    validationErrors,
    customValidationErrorTextFormatter,
    formId,
    refCallback,
  });

  // Handle changes to current value prop.
  useEffect(() => {
    // clone to prevent modifying input value.
    const clonedValue = cloneDeep(savedValue);
    dispatch(actions.setNewValueFromParentAction({ value: clonedValue }));
  }, [savedValue]);

  // Handle changes to local copy of form data.
  useEffect(() => {
    if (onFormValueChange && state.currentValue !== state.originalValue) {
      onFormValueChange(
        state.currentValue,
        getShallowDiffObject(state.originalValue, state.currentValue)
      );
    }
  }, [onFormValueChange, state.currentValue, state.originalValue]);

  // Handle changes to validationErrors that should be displayed.
  useEffect(() => {
    dispatch(actions.setFormValidationErrors(validationErrors));
  }, [validationErrors]);

  useEffect(() => {
    if (onFormSchemaChange) {
      onFormSchemaChange(state.formSchema);
    }
  }, [onFormSchemaChange, state.formSchema]);

  return (
    <FormStateContext.Provider value={state}>
      <FormDispatchContext.Provider value={dispatch}>
        {children}
      </FormDispatchContext.Provider>
    </FormStateContext.Provider>
  );
}

/**
 * Hook that allows a component to access the form's state.
 *
 * @template T
 * @returns {FormState<T>}
 */
function useFormState<T extends object>(): FormState<T> {
  const context = useContext(FormStateContext);
  if (context === undefined) {
    throw new Error(
      "useFormState must be used within a form state context provider."
    );
  }

  return context;
}

/**
 * Hook that allows a component to dispatch actions for a form.
 *
 * @returns {Dispatch}
 */
function useFormDispatch(): Dispatch {
  const context = useContext(FormDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useFormDispatch must be used within a form dispatch context provider."
    );
  }

  return context;
}

export { FormContextProvider, useFormState, useFormDispatch };
